/**
 * Character name validation rules for the payment form.
 *
 * The rules are stored on the backend under config key `character_name.rules`
 * (public=1) so the admin panel can tune them without redeploying. This module
 * mirrors `App\Libraries\CharacterNameRules` (PHP) so the frontend and
 * backend build the same regex from the same JSON.
 */

export const CONFIG_KEY = 'character_name.rules';

// Map of special-symbol identifier → JS-regex-escaped character.
// Keep the keys and order in sync with the PHP SPECIAL_MAP.
export const SPECIAL_MAP = Object.freeze({
  plus: '+',
  minus: '\\-',
  equals: '=',
  slash: '/',
  backslash: '\\\\',
  pipe: '|',
  ampersand: '&',
  asterisk: '*',
  tilde: '~',
  caret: '\\^',
  paren_open: '(',
  paren_close: ')',
  bracket_open: '\\[',
  bracket_close: '\\]',
  underscore: '_',
});

const MIN_LENGTH_BOUND = 1;
const MAX_LENGTH_BOUND = 32;

export const SPECIAL_GLYPHS = Object.freeze({
  plus: '+',
  minus: '-',
  equals: '=',
  slash: '/',
  backslash: '\\',
  pipe: '|',
  ampersand: '&',
  asterisk: '*',
  tilde: '~',
  caret: '^',
  paren_open: '(',
  paren_close: ')',
  bracket_open: '[',
  bracket_close: ']',
  underscore: '_',
});

export const DEFAULT_RULES = Object.freeze({
  latin: true,
  digits: true,
  cyrillic: true,
  min_length: 2,
  max_length: 16,
  specials: Object.freeze(
    Object.keys(SPECIAL_MAP).reduce((acc, name) => {
      acc[name] = name !== 'underscore';
      return acc;
    }, {})
  ),
});

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

export function normalizeRules(input) {
  let source = input;
  if (typeof input === 'string' && input.length > 0) {
    try {
      source = JSON.parse(input);
    } catch (e) {
      source = null;
    }
  }
  if (!source || typeof source !== 'object') {
    return JSON.parse(JSON.stringify(DEFAULT_RULES));
  }

  const rules = {
    latin: Boolean(source.latin ?? DEFAULT_RULES.latin),
    digits: Boolean(source.digits ?? DEFAULT_RULES.digits),
    cyrillic: Boolean(source.cyrillic ?? DEFAULT_RULES.cyrillic),
    min_length: clamp(
      parseInt(source.min_length ?? DEFAULT_RULES.min_length, 10) || DEFAULT_RULES.min_length,
      MIN_LENGTH_BOUND,
      MAX_LENGTH_BOUND
    ),
    max_length: clamp(
      parseInt(source.max_length ?? DEFAULT_RULES.max_length, 10) || DEFAULT_RULES.max_length,
      MIN_LENGTH_BOUND,
      MAX_LENGTH_BOUND
    ),
    specials: {},
  };

  if (rules.max_length < rules.min_length) {
    rules.max_length = rules.min_length;
  }

  const specialsInput =
    source.specials && typeof source.specials === 'object' ? source.specials : {};

  Object.keys(SPECIAL_MAP).forEach(name => {
    rules.specials[name] = Object.prototype.hasOwnProperty.call(specialsInput, name)
      ? Boolean(specialsInput[name])
      : DEFAULT_RULES.specials[name];
  });

  return rules;
}

export function buildCharacterNameRegex(input) {
  const rules = normalizeRules(input);

  let charClass = '';
  if (rules.latin) charClass += 'a-zA-Z';
  if (rules.digits) charClass += '0-9';
  // Cyrillic range covers А (А) through я (я), matching the PHP builder.
  if (rules.cyrillic) charClass += 'А-я';

  Object.keys(SPECIAL_MAP).forEach(name => {
    if (rules.specials[name]) {
      charClass += SPECIAL_MAP[name];
    }
  });

  if (charClass === '') {
    // Match nothing: any input fails validation.
    return /^(?!)$/;
  }

  return new RegExp(`^[${charClass}]{${rules.min_length},${rules.max_length}}$`);
}

export function describeCharacterNameRules(input) {
  const rules = normalizeRules(input);

  const groups = [];
  if (rules.latin) groups.push('Latin');
  if (rules.digits) groups.push('digits');
  if (rules.cyrillic) groups.push('Cyrillic');

  const specials = Object.keys(SPECIAL_MAP)
    .filter(name => rules.specials[name])
    .map(name => SPECIAL_GLYPHS[name]);

  let hint = `${rules.min_length}–${rules.max_length}`;
  if (groups.length > 0) {
    hint += `, ${groups.join(', ')}`;
  }
  if (specials.length > 0) {
    hint += `, ${specials.join(' ')}`;
  }

  return hint;
}
